/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import MockAdapter from 'axios-mock-adapter/types';
import {
    CashboxesByTaxNumbersItemDto,
    CompaniesListDto,
    CompanyDetailsDto,
    CompanyStoresListDto,
} from './companiesClient.typings';
import {
    companyDtoFactory,
    companyDetailsFactory,
    companyStoreFactory,
    turnoversByObjectFactory,
    cashboxesByTaxNumbersItemFactory,
    aiClassificationsHistoryItemFactory,
} from './companiesFactories';
import { makeMockResponse } from '../mockUtils';
import { of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { CashBoxesStats } from '../cashBoxes/cashBoxesClient.typings';
import { storeCashBoxesStatsFactory } from '../stores/storesFactories';
import {
    RegistryTurnoversByPeriodDto,
    RegistryTurnoversByObjectsDto,
    AiClassificationsHistoryDto,
} from '../apiCommon.typings';
import { TurnoversByPeriodFactory } from '../cashBoxes/cashBoxesFactories';

const COMPANIES_URL = /\/companies$/;
const COMPANIES_DETAILS_URL = /\/companies\/(\w+)$/;
const TAXPAYER_ACC_HOME_URL = /\/companies\/home/;
const COMPANIES_STORES_URL = /\/companies\/(\w+)\/stores/;
const COMPANIES_CASHBOXES_STATS: RegExp = /\/companies\/(\w+)\/cashboxes\/stats/;
const COMPANIES_GET_TURNOVERS_BY_PERIOD: RegExp = /\/companies\/\w+\/turnovers/;
const COMPANIES_GET_TURNOVERS_BY_OBJECTS: RegExp = /\/companies\/\w+\/stores\/turnovers/;
const CASHBOXES_BY_TAX_NUMBERS = /\/1.0.0\/companies\/cashboxes$/;
const AI_CLASSIFICATIONS_HISTORY = /\/companies\/\w+\/classificationsHistory$/;

export function addCompanyMockApi(mockAdapter: MockAdapter): void {
    mockAdapter.onPost(COMPANIES_URL).reply((config) => {
        const body = JSON.parse(config.data);
        const data: CompaniesListDto = {
            afterKey: '234318932',
            companies: companyDtoFactory.list({ count: body.limit }),
        };

        return delayedResponse(makeMockResponse(200, data, config), 300);
    });

    mockAdapter.onGet(COMPANIES_GET_TURNOVERS_BY_OBJECTS).reply((config) => {
        const data: RegistryTurnoversByObjectsDto = {
            count: 200,
            turnovers: turnoversByObjectFactory.list({ count: 20 }),
        };

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 1500);
    });

    mockAdapter.onGet(COMPANIES_GET_TURNOVERS_BY_PERIOD).reply((config) => {
        const data: RegistryTurnoversByPeriodDto[] = TurnoversByPeriodFactory.list({ count: 12 });
        TurnoversByPeriodFactory.resetCount();

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 1500);
    });

    mockAdapter.onGet(COMPANIES_CASHBOXES_STATS).reply((config) => {
        const data: CashBoxesStats = storeCashBoxesStatsFactory.item();

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 100);
    });

    mockAdapter.onGet(COMPANIES_STORES_URL).reply((config) => {
        const data: CompanyStoresListDto = {
            count: 60,
            list: companyStoreFactory.list({ count: config.params.limit }),
        };

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 200);
    });

    mockAdapter.onGet(COMPANIES_DETAILS_URL).reply((config) => {
        const data: CompanyDetailsDto = companyDetailsFactory.item();

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 200);
    });

    mockAdapter.onGet(TAXPAYER_ACC_HOME_URL).reply((config) => {
        const data: CompanyDetailsDto = companyDetailsFactory.item();

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 200);
    });

    mockAdapter.onPost(CASHBOXES_BY_TAX_NUMBERS).reply((config) => {
        const body = JSON.parse(config.data);

        const cashboxes: CashboxesByTaxNumbersItemDto[] = cashboxesByTaxNumbersItemFactory.list({ count: body.limit });

        let updatedCashboxes: CashboxesByTaxNumbersItemDto[];
        if (body.cashBoxTaxNumbers && body.cashBoxTaxNumbers.length !== 0) {
            updatedCashboxes = cashboxes.map((item, index) => {
                return { ...item, taxNumber: body.cashBoxTaxNumbers[index] };
            });
        }
        if (body.deviceFactoryNumbers && body.deviceFactoryNumbers.length !== 0) {
            updatedCashboxes = cashboxes.map((item, index) => {
                return { ...item, deviceFactoryNumber: body.deviceFactoryNumbers[index] };
            });
            updatedCashboxes.splice(0, 1);
        }

        return delayedResponse(
            makeMockResponse(
                200,
                {
                    afterKeyTaxNumber: 'someAfterKey',
                    cashBoxes: updatedCashboxes ? updatedCashboxes : cashboxes,
                },
                config,
            ),
            300,
        );
    });

    mockAdapter.onGet(AI_CLASSIFICATIONS_HISTORY).reply((config) => {
        const data: AiClassificationsHistoryDto = {
            count: config.params.limit * 3,
            items: aiClassificationsHistoryItemFactory.list({ count: config.params.limit }),
        };

        return delayedResponse(makeMockResponse(200, data, config), 300);
    });
}

export function delayedResponse<T>(data: T, delayMs: number): Promise<T> {
    return of(data).pipe(delay(delayMs)).toPromise();
}
